import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { controllerProps } from "@/components/forms/ariaController.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import type { LocationInputs } from "@/modules/location/LocationField.tsx";
import { getCurrentCommitment, hasFutureCommitment } from "@/modules/metal/commitment/commitment.ts";
import { commitmentCreateMutation, existingLeaseCommitmentBody } from "@/modules/metal/commitment/commitmentApi.ts";
import type { MetalListItemWithPrice } from "@/modules/metal/joinPricesQuery.ts";
import { MetalOfferSection } from "@/modules/metal/offer/MetalOfferSection.tsx";
import { periodLabels } from "@/modules/metal/offer/period.tsx";
import { useMetalOffers } from "@/modules/metal/offer/useMetalOffers.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { MetalCommitment, MetalCommitmentPeriodEnum } from "@warrenio/api-spec/spec.oats.gen";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

interface MetalCommitmentInputs extends LocationInputs {
    period: MetalCommitmentPeriodEnum;
}

function AddCommitmentModalContent({
    item,
    commitments,
}: {
    item: MetalListItemWithPrice;
    commitments: MetalCommitment[];
}) {
    //#region Hooks
    const { t } = useLingui();
    const { uuid, location, ip_public_v4, display_name, billing_account_id, spec } = item;
    const commitment = getCurrentCommitment(commitments, uuid);
    const futureCommitmentExists = hasFutureCommitment(commitments, uuid);
    const modifyMutation = useStandardMutation(commitmentCreateMutation);

    const offers = useMetalOffers({ location, specUuid: spec.uuid, billingAccountId: billing_account_id });
    const { specOffers, getEligibility } = offers;

    const defaultPeriod =
        commitment != null && specOffers.some((item) => item.period_hours === commitment.period_hours)
            ? commitment.period_hours
            : specOffers[0]?.period_hours;

    const onSubmit: SubmitHandler<MetalCommitmentInputs> = async (inputs) => {
        await modifyMutation.mutateAsync({
            body: existingLeaseCommitmentBody({
                period_hours: inputs.period,
                billing_account_id,
                metal_lease_uuid: uuid,
            }),
            location,
        });
    };

    const form = useForm<MetalCommitmentInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            period: defaultPeriod,
        },
    });
    //#endregion

    const { control } = form;

    const eligibility = getEligibility(form.watch("period"));

    return (
        <WModalForm
            title={t`Reservations`}
            label={t`Add`}
            form={form}
            // XXX: Should also explain the reason for LIMITED
            isActionDisabled={futureCommitmentExists || eligibility.isBlockedForLimited}
            footerNotice={
                futureCommitmentExists
                    ? t`You cannot add another reservation because a future reservation already exists.`
                    : undefined
            }
            onSubmit={onSubmit}
        >
            <div className="hidden md:flex gap-3 items-center justify-between lightBlock mb-3 w-700px">
                <MaskIcon className="size-2.5rem mask-icon jp-host-icon text-primary" />

                <div className="flex-grow VStack gap-0.5 children:min-h-1.5em">
                    <div>
                        <b>{display_name}</b> - <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                    </div>
                    <div className="color-muted">
                        {ip_public_v4} / {location}
                    </div>
                </div>

                <div className="text-right">
                    <div className="text-muted font-size-small">
                        <Trans>Current billing cycle</Trans>
                    </div>
                    {periodLabels[commitment?.period_hours ?? 0]}
                </div>
            </div>

            <Controller
                control={control}
                name="period"
                {...controllerProps({ isRequired: true })}
                render={({ field, fieldState: { error } }) => (
                    <MetalOfferSection
                        variant="modal"
                        {...offers}
                        value={field.value}
                        errorMessage={error?.message}
                        onChange={field.onChange}
                    />
                )}
            />
        </WModalForm>
    );
}

export function AddCommitmentModal({
    item,
    commitments,
}: {
    item: MetalListItemWithPrice;
    commitments: MetalCommitment[];
}) {
    return (
        <WModal
            button={
                <WButton color="primary" size="bar" variant="border" icon="jp-icon-add" action={undefined}>
                    <Trans>Add new reservation</Trans>
                </WButton>
            }
        >
            <AddCommitmentModalContent item={item} commitments={commitments} />
        </WModal>
    );
}
